import {
  type Alert,
  type Entity,
  formatTicker,
  type KgInsight,
  type KgInsightSubscriptionCategory,
} from '@api';
import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { getCardDate } from '@terminal/utils/card-utils/card-utils';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import sharedStyles from '../../AlertsCard.module.scss';
import styles from './AlertsKgInsightCard.module.scss';

export interface AlertsKgInsightCardProps {
  alertEvent: Alert;
  dropdown?: ReactNode;
  entity?: Entity;
  insight?: Pick<KgInsight, 'subtitle' | 'title'>;
  category: KgInsightSubscriptionCategory | undefined;
  isLoading: boolean;
}

export const AlertsKgInsightCard = ({
  alertEvent,
  dropdown,
  entity,
  insight,
  category,
  isLoading,
}: AlertsKgInsightCardProps) => {
  const { t } = useTranslation('alerts', { useSuspense: false });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '60px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!entity || !insight || !category) {
    return null;
  }

  const isRecap = category === 'earnings_recap';
  const headerTitle = isRecap
    ? t('alerts:kgInsightCard.recapAvailable')
    : t('alerts:kgInsightCard.previewAvailable');
  const iconName = isRecap
    ? 'EarningsRecapBorderless'
    : 'EarningsPreviewBorderless';

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <span
            className={clsx(styles.headerTitle, {
              [styles.read]: alertEvent.read,
            })}
          >
            {headerTitle}
          </span>
        </div>
        <div className={sharedStyles.headerRight}>
          <span className={sharedStyles.dateSpan}>
            {getCardDate(new Date(alertEvent.created_at))}
          </span>
          {dropdown}
        </div>
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <AssetLogoContainer
            entity={entity}
            size={32}
            shouldUseBorderOnFallback
          />
          <div className={sharedStyles.logoBadge}>
            <Icon
              iconName={iconName}
              size={12}
              viewBox="0 0 12 12"
              fillColor="var(--icon-decorative-purple)"
            />
          </div>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={clsx(styles.title, {
              [styles.read]: alertEvent.read,
            })}
          >
            {formatTicker(entity)} - {insight.title}
          </p>
          <p className={styles.subtitle}>{insight.subtitle}</p>
        </div>
      </div>
    </>
  );
};
